import {name, Panes, prefixWithSeparator, RelationAutocomplete, Select, TextField} from '@management-ui/core';
import {InputAdornment} from '@mui/material';
import moment from 'moment';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {ServiceContext} from '../../../../components/Services';
import {useOptions} from '../../../../hooks';
import RegistrationLookup from '../../components/RegistrationLookup';
import orderOptions from '../OrderJobForm/orderOptions';

const Fields = ({vehicle = {}, order, company: jobCompany, prefix = '', children}) => {
  const services = useContext(ServiceContext);
  const {/** @var {function(string): string} */getValues, setValue, watch} = useFormContext();

  const companies = useRef([]);
  const [company, setCompany] = useState(jobCompany ?? vehicle?.company ?? null);
  const [companyContacts, setCompanyContacts] = useState(jobCompany?.contacts ?? vehicle?.company?.contacts ?? []);

  // noinspection JSCheckFunctionSignatures
  const selectedCompanyID = watch(`${prefixWithSeparator(prefix)}company_id`, jobCompany?.id ?? vehicle?.company?.id ?? null);

  const selectedContactID = watch(`${prefixWithSeparator(prefix)}contact_id`, vehicle?.contact?.id ?? null);

  useEffect(() => {
    if (!order) {
      if (selectedCompanyID) {
        if (`${vehicle?.company?.id}` === `${selectedCompanyID}`) {
          setCompany(vehicle.company);
        } else {
          const retrieved = companies.current.find(({id}) => `${id}` === `${selectedCompanyID}`)
          if (retrieved) {
            setCompany(retrieved);
          }
        }
      } else {
        setCompany(null);
      }
    }
  }, [order, selectedCompanyID, vehicle]);

  useEffect(() => {
    if (!order) {
      setCompanyContacts((company?.contacts ?? []).map((contact) => ({
        value: contact.id,
        title: `${name(contact)}${contact.is_end_user ? ' (End User)' : ''}`,
      })));
    }
  }, [order, company]);

  useEffect(() => {
    if (!order && company) {
      if (selectedContactID) {
        const selectedContact = companyContacts.find(({value}) => `${value}` === `${selectedContactID}`);
        if (selectedContact) {
          setValue(
            `${prefixWithSeparator(prefix)}contact_name`,
            `${name(selectedContact)}${selectedContact.is_end_user ? ' (End User)' : ''}`
          );
        }
      }
    }
  }, [order, company, companyContacts, selectedContactID, setValue, prefix]);

  const [years, setYears] = useState([]);

  const [orderCompanies, setOrderCompanies] = useState([]);
  const [orderContacts, setOrderContacts] = useState([]);

  useEffect(() => {
    if (years.length < 1) {
      const list = [];
      const current = moment().year();
      for (let y = current; y >= current - 30; y--) {
        list.push({value: `${y}-01-01T00:00:00+00:00`, title: `${y}`});
      }
      setYears(list);
    }
  }, [years]);

  useEffect(() => {
    if (order) {
      const {companies, contacts} = orderOptions(order);
      setOrderCompanies(companies);
      setOrderContacts(contacts);
    }
  }, [order]);

  const types = useOptions('vehicleTypes', useCallback((list) => list.map(type => ({
    value: type.id,
    title: type.name
  })), []));

  const fuels = useOptions('fuelTypes', useCallback((list) => list.map(type => ({
    value: type.id,
    title: type.name
  })), []));

  const batteries = useOptions('batteryTypes', useCallback((list) => list.map(type => ({
    value: type.id,
    title: type.name
  })), []));

  return (
    <Panes
      entity={vehicle}
      prefix={prefix}
      panes={[
        {
          title: 'Vehicle Identification',
          fields: [
            ...(order ? [
              <Select
                name="company_id"
                prefix={prefix}
                options={orderCompanies}
                label="Company"
                displayEmpty={true}
                rules={{
                  validate: () => {
                    const fieldPrefix = prefixWithSeparator(prefix);
                    if (!getValues(`${fieldPrefix}company_id`) && !getValues(`${fieldPrefix}contact_id`)) {
                      return 'Please select either a company or a contact';
                    }
                    return true;
                  }
                }}
              />,
              <Select
                name="contact_id"
                prefix={prefix}
                options={orderContacts}
                label="Contact"
                displayEmpty={true}
              />
            ] : [
              <RelationAutocomplete
                valueName="company_id"
                titleName="company_name"
                prefix={prefix}
                label="Company"
                loadOptions={query => (
                  services.company.getCompanies({filter: {search: query}, include: 'contacts'}, 1, 50)
                    .then(response => {
                      companies.current = response.data;
                      return response.data.map(company => ({title: company.name, value: company.id}));
                    })
                    .catch(() => [])
                )}
              />,
              ...(company ? [
                <Select
                  name="contact_id"
                  prefix={prefix}
                  options={companyContacts}
                  label="Contact"
                />,
              ] : [
                <RelationAutocomplete
                  valueName="contact_id"
                  titleName="contact_name"
                  prefix={prefix}
                  label="Contact"
                  loadOptions={query => (
                    services.contact.getContacts({filter: {search: query}}, 1, 50)
                      .then(response => response.data.map(contact => ({title: name(contact), value: contact.id})))
                      .catch(() => [])
                  )}
                />
              ])
            ]),
            <TextField
              name="registration"
              prefix={prefix}
              label="Vehicle Registration"
              fieldProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <RegistrationLookup name="registration" prefix={prefix}/>
                    </InputAdornment>
                  )
                }
              }}
            />,
            <TextField
              name="vin"
              prefix={prefix}
              label="VIN Number"
            />,
            <TextField
              name="make"
              prefix={prefix}
              label="Make"
            />,
            <TextField
              name="model"
              prefix={prefix}
              label="Model"
            />,
            <Select
              name="vehicle_type_id"
              prefix={prefix}
              options={types}
              label="Vehicle Type"
            />,
          ]
        }, {
          title: 'Vehicle Details',
          fields: [
            <TextField
              name="colour"
              prefix={prefix}
              label="Colour"
            />,
            <Select
              name="manufactured_on"
              prefix={prefix}
              options={years}
              label="Year of Manufacture"
            />,
            <Select
              name="fuel_type_id"
              prefix={prefix}
              options={fuels}
              label="Fuel Type"
            />,
            <Select
              name="battery_type_id"
              prefix={prefix}
              options={batteries}
              label="Battery Type"
            />,
            <TextField
              name="scorpion_reference"
              prefix={prefix}
              label="Scorpion ID"
            />,
            <TextField
              name="aliasList"
              prefix={prefix}
              label="Registration Aliases (one per line)"
              fieldProps={{multiline: true}}
            />,
          ]
        }
      ]}
      title={null}
    >{children}</Panes>
  );
};

export default Fields;
